import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  NumberInput,
  SegmentedControl,
  Slider,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type AnchorInput,
  connectMail,
  createAnchor,
  deleteAnchor,
  disconnectMail,
  getCriteria,
  getMailStatus,
  getNotificationStatus,
  getSettings,
  type MailCreds,
  previewGeocode,
  putCriteria,
  type GeocodePreview,
  testMail,
  testNotification,
  type UserAnchor,
  updateAnchor,
  updateSettings,
  type WohnungCriteria,
} from '../lib/api.js';
import { disablePush, enablePush, pushSupported } from '../lib/push.js';

const EMPTY_ANCHOR: AnchorInput = {
  displayName: '',
  address: '',
  icon: '📍',
  role: 'secondary',
  scoringWeightFactor: 1,
  maxCommuteMin: null,
  profileScope: 'both',
  active: true,
};

const ICON_CHOICES = ['💼', '🏠', '👵', '👶', '🏢', '🎓', '⛪', '📍'];

export function Settings(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: getSettings });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<UserAnchor | null>(null);

  const settingsMut = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => notifications.show({ message: `Fehler: ${String(e)}`, color: 'red' }),
  });

  const delMut = useMutation({
    mutationFn: (id: number) => deleteAnchor(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      notifications.show({ message: 'Anker gelöscht', color: 'gray' });
    },
    onError: (e) => notifications.show({ message: `Fehler: ${String(e)}`, color: 'red' }),
  });

  if (isLoading || !data) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  const { settings, anchors } = data;

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Title order={1}>Einstellungen</Title>
        <Text c="dimmed" size="sm">
          Deine Suchprofile, Pendel-Ziele und Benachrichtigungen. Änderungen greifen ab dem
          nächsten Scoring-Lauf.
        </Text>
      </Stack>

      {/* (Alte „Wohnung/Büro"-Toggles entfernt — abgelöst durch benutzerdefinierte
          Such-Profile, die man je eigenständig aktivieren/pausieren kann.) */}

      {/* Benachrichtigung. Push feuert jetzt genau dann, wenn ein Inserat Phase 3 (swipe-würdig)
          erreicht — dieselbe Schwelle wie die Swipe-Queue, pro Such-Profil. Kein separater
          Push-Schwellen-Slider mehr (das war der verwirrende 0,65-Wert). */}
      <Card padding="lg">
        <Stack gap={6}>
          <Title order={4}>Benachrichtigung</Title>
          <Text size="sm" c="dimmed">
            Du wirst benachrichtigt, sobald ein Inserat <b>Phase 3</b> erreicht (swipe-würdig) —
            also dieselbe Schwelle wie deine Swipe-Queue. Diese <b>Phase-3-Schwelle</b> (und die
            Phase-1-Schwelle für die KI-Analyse) stellst du <b>pro Such-Profil</b> unter
            „Such-Profile & Kriterien" ein.
          </Text>
        </Stack>
      </Card>

      {/* Frische — statt automatischer Alters-Abwertung: ab hier gilt ein Inserat als „älter". */}
      <Card padding="lg">
        <Group justify="space-between" align="center" wrap="nowrap" gap="md">
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Title order={4}>Frische</Title>
            <Text size="sm" c="dimmed">
              Inserate werden nicht mehr automatisch abgewertet. Beim Swipen kommen erst alle
              frischen, danach die älteren mit Warnung. Ab wie vielen Tagen gilt ein Inserat als
              „älter"?
            </Text>
          </Stack>
          <NumberInput
            value={settings.freshDays ?? 4}
            min={1}
            max={60}
            step={1}
            w={110}
            suffix=" Tage"
            onChange={(v) => settingsMut.mutate({ freshDays: typeof v === 'number' ? v : 4 })}
          />
        </Group>
      </Card>

      {/* Suchkriterien → eigene Seite (Miete + Kauf, mehrere Profile) */}
      <Card padding="lg" component={Link} to="/search-profiles" style={{ textDecoration: 'none' }}>
        <Group justify="space-between" align="center" wrap="nowrap">
          <Stack gap={2}>
            <Title order={4}>Such-Profile & Kriterien →</Title>
            <Text size="sm" c="dimmed">
              Budget, Fläche, Zimmer, Baujahr, Lage & Gewichte — pro Suche (Miete/Kauf, Wohnung/Haus).
            </Text>
          </Stack>
          <Text size="xl">🎯</Text>
        </Group>
      </Card>

      {/* Anker */}
      <Card padding="lg">
        <Stack gap="md">
          <Group justify="space-between" align="baseline" wrap="wrap">
            <Title order={4}>Pendel-Ziele (Anker)</Title>
            <Button
              size="xs"
              onClick={() => {
                setEditing(null);
                setEditorOpen(true);
              }}
            >
              + Anker hinzufügen
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Arbeit, Zuhause, Eltern … — von diesen Orten wird die Pendelzeit berechnet und
            fließt in den Score ein. „Primär" ist dein wichtigstes Ziel, „sekundär" die weiteren
            (z. B. Eltern, Geschwister) — deren Gewicht stellst du je Anker ein.
          </Text>

          {anchors.length === 0 && (
            <Card withBorder padding="md">
              <Text size="sm" c="dimmed" ta="center">
                Noch keine Anker. Füge mindestens deinen Haupt-Pendelort hinzu, damit
                Pendelzeiten berechnet werden können.
              </Text>
            </Card>
          )}

          <Stack gap="sm">
            {anchors.map((a) => (
              <Card key={a.id} withBorder padding="sm">
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Text style={{ fontSize: '1.4rem' }}>{a.icon}</Text>
                    <Stack gap={2} style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="wrap">
                        <Text fw={600}>{a.displayName}</Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={a.role === 'primary' ? 'blue' : 'gray'}
                          tt="none"
                        >
                          {a.role === 'primary' ? 'primär' : 'sekundär'}
                        </Badge>
                        {a.scoringWeightFactor !== 1 && (
                          <Badge size="xs" variant="light" color="orange" tt="none">
                            Gewicht ×{a.scoringWeightFactor}
                          </Badge>
                        )}
                        {a.maxCommuteMin != null && a.maxCommuteMin > 0 && (
                          <Badge size="xs" variant="light" color="red" tt="none">
                            No-Go ab {a.maxCommuteMin} min
                          </Badge>
                        )}
                        {!a.active && (
                          <Badge size="xs" variant="light" color="red" tt="none">
                            inaktiv
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                        {a.address}
                      </Text>
                      {a.lat !== null && a.lng !== null ? (
                        <Text size="xs" c="teal.7">
                          ✓ geocodet ({a.lat.toFixed(4)}, {a.lng.toFixed(4)})
                        </Text>
                      ) : (
                        <Text size="xs" c="red.7">
                          ⚠ nicht geocodet
                        </Text>
                      )}
                    </Stack>
                  </Group>
                  <Group gap={4} wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => {
                        setEditing(a);
                        setEditorOpen(true);
                      }}
                      aria-label="Bearbeiten"
                    >
                      ✏️
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      onClick={() => {
                        if (confirm(`Anker „${a.displayName}" löschen?`)) delMut.mutate(a.id);
                      }}
                      aria-label="Löschen"
                    >
                      🗑
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            ))}
          </Stack>
        </Stack>
      </Card>

      {/* Postfach */}
      <MailboxCard />

      {/* Benachrichtigung */}
      <NotificationCard
        emailNotify={settings.emailNotify}
        ntfyTopic={settings.ntfyTopic}
        onSetEmail={(v) => settingsMut.mutate({ emailNotify: v })}
        onSetNtfy={(v) => settingsMut.mutate({ ntfyTopic: v })}
      />

      <AnchorEditor
        opened={editorOpen}
        anchor={editing}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          qc.invalidateQueries({ queryKey: ['settings'] });
        }}
      />
    </Stack>
  );
}

function MailboxCard(): JSX.Element {
  const qc = useQueryClient();
  const { data: status, isLoading } = useQuery({ queryKey: ['mail'], queryFn: getMailStatus });
  const [form, setForm] = useState<MailCreds>({
    imapHost: 'imap.gmail.com',
    imapPort: 993,
    mailUser: '',
    appPassword: '',
  });
  const [editing, setEditing] = useState(false);

  const testMut = useMutation({
    mutationFn: () => testMail(form),
    onSuccess: () => notifications.show({ message: '✓ Verbindung erfolgreich', color: 'green' }),
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });
  const connectMut = useMutation({
    mutationFn: () => connectMail(form),
    onSuccess: () => {
      notifications.show({ message: '✓ Postfach verbunden', color: 'green' });
      setEditing(false);
      setForm({ ...form, appPassword: '' });
      qc.invalidateQueries({ queryKey: ['mail'] });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });
  const disconnectMut = useMutation({
    mutationFn: disconnectMail,
    onSuccess: () => {
      notifications.show({ message: 'Postfach getrennt', color: 'gray' });
      qc.invalidateQueries({ queryKey: ['mail'] });
    },
  });

  if (isLoading || !status) return <Card padding="lg"><Center py="md"><Loader size="sm" /></Center></Card>;

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Title order={4}>Postfach</Title>

        {status.isDefaultMailbox ? (
          <Text size="sm" c="dimmed">
            Dieses Konto nutzt das zentrale Sammel-Postfach (Betreiber-Setup). Die
            Postfach-Einstellung ist hier deaktiviert.
          </Text>
        ) : (
          <>
            <Text size="sm" c="dimmed">
              Verbinde dein E-Mail-Postfach, damit betongold deine Portal-Alert-Mails
              (willhaben, ImmoScout, …) automatisch einliest. Wir empfehlen ein eigenes
              <b> Gmail-App-Passwort</b> — dein normales Passwort funktioniert nicht.
            </Text>

            {status.connected && !editing ? (
              <Card withBorder padding="md">
                <Group justify="space-between" wrap="wrap" gap="sm">
                  <Stack gap={2}>
                    <Group gap={6}>
                      <Badge color="green" variant="light" tt="none">✓ verbunden</Badge>
                      <Text fw={600}>{status.mailUser}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {status.imapHost}:{status.imapPort}
                      {status.lastPollAt &&
                        ` · zuletzt abgerufen ${new Date(status.lastPollAt).toLocaleString('de-AT')}`}
                    </Text>
                    {status.lastError && (
                      <Text size="xs" c="red.7">⚠ {status.lastError}</Text>
                    )}
                  </Stack>
                  <Group gap="xs">
                    <Button size="xs" variant="light" onClick={() => setEditing(true)}>
                      Ändern
                    </Button>
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      onClick={() => {
                        if (confirm('Postfach wirklich trennen? Neue Mails werden dann nicht mehr eingelesen.'))
                          disconnectMut.mutate();
                      }}
                    >
                      Trennen
                    </Button>
                  </Group>
                </Group>
              </Card>
            ) : (
              <Stack gap="sm">
                <TextInput
                  label="E-Mail-Adresse (IMAP-Login)"
                  placeholder="dein.name@gmail.com"
                  value={form.mailUser}
                  onChange={(e) => setForm({ ...form, mailUser: e.currentTarget.value })}
                />
                <TextInput
                  label="App-Passwort"
                  type="password"
                  placeholder="16-stelliges Google-App-Passwort"
                  value={form.appPassword}
                  onChange={(e) => setForm({ ...form, appPassword: e.currentTarget.value })}
                />
                <Group grow>
                  <TextInput
                    label="IMAP-Server"
                    value={form.imapHost}
                    onChange={(e) => setForm({ ...form, imapHost: e.currentTarget.value })}
                  />
                  <NumberInput
                    label="Port"
                    value={form.imapPort}
                    onChange={(v) => setForm({ ...form, imapPort: typeof v === 'number' ? v : 993 })}
                    min={1}
                    max={65535}
                  />
                </Group>
                <Text size="xs" c="dimmed">
                  Gmail: App-Passwort unter myaccount.google.com → Sicherheit → 2-Faktor →
                  App-Passwörter erstellen. IMAP muss in den Gmail-Einstellungen aktiviert sein.
                </Text>
                <Group justify="flex-end" gap="xs">
                  {editing && (
                    <Button variant="subtle" color="gray" size="sm" onClick={() => setEditing(false)}>
                      Abbrechen
                    </Button>
                  )}
                  <Button
                    variant="light"
                    size="sm"
                    onClick={() => testMut.mutate()}
                    loading={testMut.isPending}
                    disabled={!form.mailUser || !form.appPassword}
                  >
                    Testen
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => connectMut.mutate()}
                    loading={connectMut.isPending}
                    disabled={!form.mailUser || !form.appPassword}
                  >
                    Verbinden
                  </Button>
                </Group>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

function NotificationCard({
  emailNotify,
  ntfyTopic,
  onSetEmail,
  onSetNtfy,
}: {
  emailNotify: boolean;
  ntfyTopic: string | null;
  onSetEmail: (v: boolean) => void;
  onSetNtfy: (v: string | null) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['notif-status'], queryFn: getNotificationStatus });
  const [ntfyLocal, setNtfyLocal] = useState(ntfyTopic ?? '');
  const ntfyDirty = ntfyLocal.trim() !== (ntfyTopic ?? '');
  const supported = pushSupported();

  const pushMut = useMutation({
    mutationFn: async (enable: boolean) => (enable ? enablePush() : disablePush()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notif-status'] });
      qc.invalidateQueries({ queryKey: ['settings'] });
      notifications.show({ message: 'Web-Push aktualisiert', color: 'green' });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  const testMut = useMutation({
    mutationFn: testNotification,
    onSuccess: (r) => {
      const ok = r.perChannel.filter((c) => c.ok).map((c) => c.kind);
      const fail = r.perChannel.filter((c) => !c.ok);
      notifications.show({
        message: r.anyDelivered
          ? `✓ Test gesendet über: ${ok.join(', ')}${fail.length ? ` (Fehler: ${fail.map((f) => f.kind).join(', ')})` : ''}`
          : 'Kein aktiver Kanal — aktiviere mindestens einen unten.',
        color: r.anyDelivered ? 'green' : 'orange',
      });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="baseline" wrap="wrap">
          <Title order={4}>Benachrichtigung</Title>
          <Button size="xs" variant="light" onClick={() => testMut.mutate()} loading={testMut.isPending}>
            🧪 Test senden
          </Button>
        </Group>

        {/* Web-Push (PWA) */}
        <Stack gap={4}>
          <Group justify="space-between">
            <Stack gap={0}>
              <Text fw={600}>📲 Web-Push (App)</Text>
              <Text size="xs" c="dimmed">
                Direkt aufs Handy — keine Extra-App nötig, nutzt die betongold-PWA.
              </Text>
            </Stack>
            <Switch
              checked={(status?.webpushEnabled ?? false) && (status?.webpushDevices ?? 0) > 0}
              onChange={(e) => pushMut.mutate(e.currentTarget.checked)}
              disabled={!supported || pushMut.isPending}
              onLabel="AN"
              offLabel="AUS"
              size="md"
              color="violet"
            />
          </Group>
          {!supported && (
            <Text size="xs" c="orange.7">
              ⚠ Web-Push braucht HTTPS — funktioniert erst auf der öffentlichen betongold-Domain,
              nicht über die lokale IP.
            </Text>
          )}
          {supported && (status?.webpushDevices ?? 0) > 0 && (
            <Text size="xs" c="teal.7">✓ {status?.webpushDevices} Gerät(e) verbunden</Text>
          )}
        </Stack>

        {/* E-Mail */}
        <Group justify="space-between">
          <Stack gap={0}>
            <Text fw={600}>✉️ E-Mail</Text>
            <Text size="xs" c="dimmed">Treffer an deine Login-Adresse.</Text>
          </Stack>
          <Switch
            checked={emailNotify}
            onChange={(e) => onSetEmail(e.currentTarget.checked)}
            onLabel="AN"
            offLabel="AUS"
            size="md"
            color="blue"
          />
        </Group>

        {/* ntfy */}
        <Stack gap={6}>
          <Text fw={600}>🔔 ntfy.sh</Text>
          <Text size="xs" c="dimmed">
            Dein persönliches ntfy-Topic. ntfy-App installieren, denselben Namen abonnieren.
          </Text>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              placeholder="z. B. betongold-kamil-4f7a"
              value={ntfyLocal}
              onChange={(e) => setNtfyLocal(e.currentTarget.value)}
            />
            <Button
              size="sm"
              disabled={!ntfyDirty}
              onClick={() => onSetNtfy(ntfyLocal.trim() === '' ? null : ntfyLocal.trim())}
            >
              Speichern
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Card>
  );
}

function AnchorEditor({
  opened,
  anchor,
  onClose,
  onSaved,
}: {
  opened: boolean;
  anchor: UserAnchor | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [form, setForm] = useState<AnchorInput>(EMPTY_ANCHOR);
  const [preview, setPreview] = useState<GeocodePreview | null>(null);
  const [initialized, setInitialized] = useState(false);

  // Formular beim Öffnen mit dem zu editierenden Anker (oder leer) befüllen.
  if (opened && !initialized) {
    setForm(
      anchor
        ? {
            displayName: anchor.displayName,
            address: anchor.address,
            icon: anchor.icon,
            role: anchor.role,
            scoringWeightFactor: anchor.scoringWeightFactor,
            maxCommuteMin: anchor.maxCommuteMin,
            profileScope: anchor.profileScope,
            active: anchor.active,
          }
        : EMPTY_ANCHOR,
    );
    setPreview(
      anchor && anchor.lat !== null && anchor.lng !== null
        ? { lat: anchor.lat, lng: anchor.lng, precision: 'gespeichert', formattedAddress: anchor.address }
        : null,
    );
    setInitialized(true);
  }
  if (!opened && initialized) setInitialized(false);

  const geoMut = useMutation({
    mutationFn: () => previewGeocode(form.address),
    onSuccess: (p) => setPreview(p),
    onError: () =>
      notifications.show({ message: 'Adresse nicht gefunden — bitte präzisieren', color: 'red' }),
  });

  const saveMut = useMutation({
    mutationFn: () => (anchor ? updateAnchor(anchor.id, form) : createAnchor(form)),
    onSuccess: () => {
      notifications.show({ message: 'Anker gespeichert', color: 'green' });
      onSaved();
    },
    onError: (e) => notifications.show({ message: `Fehler: ${String(e)}`, color: 'red' }),
  });

  const canSave = form.displayName.trim().length > 0 && form.address.trim().length >= 3;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={anchor ? 'Anker bearbeiten' : 'Anker hinzufügen'}
      centered
      size="md"
    >
      <Stack gap="md">
        <TextInput
          label="Bezeichnung"
          placeholder="z. B. Arbeit, Zuhause, Eltern (Mama)"
          value={form.displayName}
          onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
          required
        />

        <Stack gap={4}>
          <Text size="sm" fw={500}>Icon</Text>
          <Group gap={6}>
            {ICON_CHOICES.map((ic) => (
              <ActionIcon
                key={ic}
                variant={form.icon === ic ? 'filled' : 'light'}
                color="violet"
                size="lg"
                onClick={() => setForm({ ...form, icon: ic })}
              >
                <span style={{ fontSize: '1.1rem' }}>{ic}</span>
              </ActionIcon>
            ))}
          </Group>
        </Stack>

        <Stack gap={4}>
          <TextInput
            label="Adresse"
            placeholder="Straße Hausnummer, PLZ Ort"
            value={form.address}
            onChange={(e) => {
              setForm({ ...form, address: e.currentTarget.value });
              setPreview(null);
            }}
            required
          />
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              onClick={() => geoMut.mutate()}
              loading={geoMut.isPending}
              disabled={form.address.trim().length < 3}
            >
              📍 Adresse prüfen
            </Button>
            {preview && (
              <Text size="xs" c="teal.7">
                ✓ {preview.formattedAddress} ({preview.lat.toFixed(4)}, {preview.lng.toFixed(4)}) ·{' '}
                {preview.precision}
              </Text>
            )}
          </Group>
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={500}>Rolle</Text>
          <SegmentedControl
            value={form.role}
            onChange={(v) => setForm({ ...form, role: v as 'primary' | 'secondary' })}
            data={[
              { label: 'Primär (Hauptziel)', value: 'primary' },
              { label: 'Sekundär (weitere)', value: 'secondary' },
            ]}
            size="xs"
          />
        </Stack>

        <NumberInput
          label="Gewichtungsfaktor"
          description="1 = normal. 0,5 = zählt halb so viel (z. B. zweiter Eltern-Ort). 0 = nur Anzeige, kein Score-Einfluss."
          value={form.scoringWeightFactor}
          onChange={(v) => setForm({ ...form, scoringWeightFactor: typeof v === 'number' ? v : 1 })}
          min={0}
          max={2}
          step={0.5}
          decimalScale={1}
        />

        <NumberInput
          label="Reisezeit-Obergrenze (Min)"
          description="Ab dieser Reisezeit (kürzeste Verbindung Auto/ÖV) wird das Objekt ein No-Go und fliegt aus der Swipe-Liste. Leer = kein Limit."
          value={form.maxCommuteMin ?? ''}
          onChange={(v) =>
            setForm({ ...form, maxCommuteMin: typeof v === 'number' && v > 0 ? v : null })
          }
          min={0}
          max={240}
          step={5}
          placeholder="kein Limit"
        />

        <Switch
          label="Anker aktiv (fließt in Pendelberechnung ein)"
          checked={form.active}
          onChange={(e) => setForm({ ...form, active: e.currentTarget.checked })}
        />

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" color="gray" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => saveMut.mutate()} disabled={!canSave} loading={saveMut.isPending}>
            Speichern
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

const numOf = (v: number | string): number => (typeof v === 'number' ? v : parseFloat(v) || 0);

/** Wichtigkeits-Regler (Multiplikator aufs Basisgewicht). 0 = Kriterium aus. */
function ImportanceSlider({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}): JSX.Element {
  return (
    <Group gap="sm" align="center" wrap="nowrap">
      <Text size="xs" c="dimmed" style={{ minWidth: 74 }}>
        Wichtigkeit
      </Text>
      <Slider
        style={{ flex: 1 }}
        value={value}
        onChange={onChange}
        min={0}
        max={3}
        step={0.25}
        color={value === 0 ? 'gray' : 'violet'}
        label={(v) => (v === 0 ? 'aus' : `${v}×`)}
        marks={[
          { value: 0, label: 'aus' },
          { value: 1, label: 'normal' },
          { value: 2, label: 'wichtig' },
          { value: 3, label: 'max' },
        ]}
      />
    </Group>
  );
}

function CriteriaBlock({
  title,
  hint,
  importance,
  onImportance,
  children,
}: {
  title: string;
  hint?: string;
  importance: number;
  onImportance: (v: number) => void;
  children?: ReactNode;
}): JSX.Element {
  return (
    <Stack gap={8} style={{ paddingBottom: 8, borderBottom: '1px solid rgba(15,23,42,0.06)' }}>
      <Text fw={600} size="sm">
        {title}
      </Text>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
      {children}
      <ImportanceSlider value={importance} onChange={onImportance} />
    </Stack>
  );
}

/** Suchkriterien-Karte (pro Konto). Lädt die Config und rendert den Editor. */
function CriteriaCard(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['criteria', 'A_wohnung'],
    queryFn: () => getCriteria('A_wohnung'),
  });
  if (isLoading || !data) {
    return (
      <Card padding="lg">
        <Center py="md">
          <Loader size="sm" />
        </Center>
      </Card>
    );
  }
  return <CriteriaEditor key={JSON.stringify(data.config)} initial={data.config} defaults={data.defaults} />;
}

function CriteriaEditor({
  initial,
  defaults,
}: {
  initial: WohnungCriteria;
  defaults: WohnungCriteria;
}): JSX.Element {
  const qc = useQueryClient();
  const [cfg, setCfg] = useState<WohnungCriteria>(initial);

  const saveMut = useMutation({
    mutationFn: (c: WohnungCriteria) => putCriteria('A_wohnung', c),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['criteria'] });
      qc.invalidateQueries({ queryKey: ['objects'] });
      notifications.show({
        message: `Suchkriterien gespeichert · ${res.rescored} Objekte neu bewertet`,
        color: 'green',
      });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  const up = <K extends keyof WohnungCriteria>(
    k: K,
    patch: Partial<WohnungCriteria[K]>,
  ): void => setCfg((c) => ({ ...c, [k]: { ...(c[k] as object), ...patch } }));

  const dirty = JSON.stringify(cfg) !== JSON.stringify(initial);
  const isDefault = JSON.stringify(cfg) === JSON.stringify(defaults);

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="baseline" wrap="wrap">
          <Title order={4}>Suchkriterien</Title>
          <Text size="xs" c="dimmed">
            Wohnung · gilt für dieses Konto
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          Schwellwerte + Wichtigkeit je Kriterium. „Wichtigkeit aus" schaltet ein Kriterium ganz
          ab. Speichern bewertet die Objekte dieses Kontos sofort neu.
        </Text>

        <CriteriaBlock
          title="💶 Budget (Miete brutto)"
          hint="Killcrit greift bei +15 % über dem Budget."
          importance={cfg.preis.importance}
          onImportance={(v) => up('preis', { importance: v })}
        >
          <Group grow>
            <NumberInput
              label="Wunschpreis (€)"
              description="volle Punkte bis hier"
              value={cfg.preis.comfortable}
              min={0}
              step={50}
              onChange={(v) => up('preis', { comfortable: numOf(v) })}
            />
            <NumberInput
              label="Budget / Max (€)"
              value={cfg.preis.budget}
              min={0}
              step={50}
              onChange={(v) => up('preis', { budget: numOf(v) })}
            />
          </Group>
        </CriteriaBlock>

        <CriteriaBlock
          title="📐 Wohnfläche (m²)"
          hint="Killcrit unter dem Minimum."
          importance={cfg.wohnflaeche.importance}
          onImportance={(v) => up('wohnflaeche', { importance: v })}
        >
          <Group grow>
            <NumberInput
              label="Minimum"
              value={cfg.wohnflaeche.min}
              min={0}
              step={5}
              onChange={(v) => up('wohnflaeche', { min: numOf(v) })}
            />
            <NumberInput
              label="Gut"
              value={cfg.wohnflaeche.good}
              min={0}
              step={5}
              onChange={(v) => up('wohnflaeche', { good: numOf(v) })}
            />
            <NumberInput
              label="Ideal"
              value={cfg.wohnflaeche.ideal}
              min={0}
              step={5}
              onChange={(v) => up('wohnflaeche', { ideal: numOf(v) })}
            />
          </Group>
        </CriteriaBlock>

        <CriteriaBlock
          title="🚪 Zimmer"
          hint="Killcrit unter dem Minimum."
          importance={cfg.zimmer.importance}
          onImportance={(v) => up('zimmer', { importance: v })}
        >
          <Group grow>
            <NumberInput
              label="Minimum"
              value={cfg.zimmer.min}
              min={1}
              step={1}
              onChange={(v) => up('zimmer', { min: numOf(v) })}
            />
            <NumberInput
              label="Ideal (volle Punkte ab)"
              value={cfg.zimmer.ideal}
              min={1}
              step={1}
              onChange={(v) => up('zimmer', { ideal: numOf(v) })}
            />
          </Group>
        </CriteriaBlock>

        <CriteriaBlock
          title="🚌 Pendel zur Arbeit (Min)"
          hint="Zu deinem Primär-Anker. Killcrit über dem Maximum."
          importance={cfg.pendelArbeit.importance}
          onImportance={(v) => up('pendelArbeit', { importance: v })}
        >
          <Group grow>
            <NumberInput
              label="Ideal (bis)"
              value={cfg.pendelArbeit.ideal}
              min={0}
              step={5}
              onChange={(v) => up('pendelArbeit', { ideal: numOf(v) })}
            />
            <NumberInput
              label="Maximum (Killcrit)"
              value={cfg.pendelArbeit.kill}
              min={0}
              step={5}
              onChange={(v) => up('pendelArbeit', { kill: numOf(v) })}
            />
          </Group>
        </CriteriaBlock>

        <CriteriaBlock
          title="👵 Pendel zu Eltern (Min)"
          hint="Blend über deine sekundären Anker."
          importance={cfg.pendelEltern.importance}
          onImportance={(v) => up('pendelEltern', { importance: v })}
        >
          <Group grow>
            <NumberInput
              label="Ideal (bis)"
              value={cfg.pendelEltern.ideal}
              min={0}
              step={5}
              onChange={(v) => up('pendelEltern', { ideal: numOf(v) })}
            />
            <NumberInput
              label="0 Punkte ab"
              value={cfg.pendelEltern.zero}
              min={0}
              step={5}
              onChange={(v) => up('pendelEltern', { zero: numOf(v) })}
            />
          </Group>
        </CriteriaBlock>

        <CriteriaBlock
          title="🌿 Außenbereich"
          importance={cfg.aussenbereich.importance}
          onImportance={(v) => up('aussenbereich', { importance: v })}
        >
          <Switch
            label="Pflicht — ohne Balkon/Terrasse/Garten ausschließen"
            checked={cfg.aussenbereich.required}
            onChange={(e) => up('aussenbereich', { required: e.currentTarget.checked })}
          />
        </CriteriaBlock>

        <CriteriaBlock
          title="📄 Unbefristet"
          importance={cfg.befristung.importance}
          onImportance={(v) => up('befristung', { importance: v })}
        />
        <CriteriaBlock
          title="⚡ Energieklasse"
          importance={cfg.energieklasse.importance}
          onImportance={(v) => up('energieklasse', { importance: v })}
        />
        <CriteriaBlock
          title="💸 Ablöse (niedrig = besser)"
          importance={cfg.abloese.importance}
          onImportance={(v) => up('abloese', { importance: v })}
        />

        <Stack gap={4}>
          <Text fw={600} size="sm">
            Weitere Ausschlüsse
          </Text>
          <Switch
            label="Sanierungsbedürftige Objekte ausschließen"
            checked={cfg.killZustandSanierung}
            onChange={(e) => setCfg((c) => ({ ...c, killZustandSanierung: e.currentTarget.checked }))}
          />
          <Switch
            label="Ab 3. Stock ohne Lift ausschließen"
            checked={cfg.killStockwerk3OhneLift}
            onChange={(e) =>
              setCfg((c) => ({ ...c, killStockwerk3OhneLift: e.currentTarget.checked }))
            }
          />
          <Switch
            label="Erdgeschoss ohne Garten ausschließen"
            checked={cfg.killEgOhneGarten}
            onChange={(e) => setCfg((c) => ({ ...c, killEgOhneGarten: e.currentTarget.checked }))}
          />
        </Stack>

        <Group justify="flex-end" gap="xs" mt="xs">
          <Button
            variant="subtle"
            color="gray"
            disabled={isDefault}
            onClick={() => setCfg(defaults)}
          >
            Auf Standard
          </Button>
          <Button
            onClick={() => saveMut.mutate(cfg)}
            disabled={!dirty || saveMut.isPending}
            loading={saveMut.isPending}
          >
            Speichern & neu bewerten
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
